import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type ProviderSubscription } from '@marketplace/contracts';
import { BillingButton } from './BillingButton.js';
import { isPastDue } from './billing.js';

/**
 * `W5-T07` §4.10 — on the billing page and on `/account`, while a renewal has failed (`PAST_DUE`) or
 * Stripe has stopped retrying (`UNPAID`): the status line, which already says to update the card, and
 * **Update card**, to the portal, where the card is changed.
 *
 * On the billing page it *is* the status line for those two, so the sentence is not said twice.
 * `null` is "no banner": a subscription that could not be read leaves the page what it was.
 */
export function PastDueBanner({
  subscription,
  locale,
}: {
  subscription: ProviderSubscription | null;
  locale: string;
}): ReactElement | null {
  const { t } = useTranslation();
  if (subscription === null || !isPastDue(subscription.status)) return null;

  return (
    <section className="mp-section" data-testid="billing-banner">
      <p className="mp-lead" data-testid="billing-status">
        {t(`billing.status.${subscription.status}`)}
      </p>
      <BillingButton locale={locale} intent="portal" label={t('billing.action.update_card')} />
    </section>
  );
}
